/**
 * How an event's category is named and drawn, in one place.
 *
 * The month grid, the legend, the table's Type column and the Event Type filter all
 * have to agree on this. They used to live inside EventCalendar, which was fine while
 * the calendar was the only thing that rendered an event; now the table and the list
 * page do too, and a second copy would drift — a chip saying "1P Local" beside a table
 * row saying "ONE_P_LOCAL" is exactly the sort of split worth preventing up front.
 */

import {
  EVENT_TYPE_ORDER,
  type Event,
  type EventLifecycleStage,
  type EventSubType,
  type EventType,
} from '../client/EventCalendarClient';

export interface TypeMeta {
  label: string;
  /** Chip / legend colour class, defined in EventCalendar.css. */
  className: string;
  /**
   * The type's pale tint, from the Figma's chip and badge fills. Same value as the
   * className's background, kept here too so a component outside the calendar (the New
   * Events type badge) colours itself without depending on EventCalendar.css being loaded.
   */
  tint: string;
}

/**
 * Label and chip colour per type. The glyph is drawn by EventTypeIcon, from the Figma.
 */
export const TYPE_META: Record<EventType, TypeMeta> = {
  ONE_P_LOCAL: { label: '1P Local', className: 'ec-chip--1p-local', tint: '#effff1' },
  ONE_P_GLOBAL: { label: '1P Global', className: 'ec-chip--1p-global', tint: '#f0fbff' },
  THREE_P_LOCAL: { label: '3P Local', className: 'ec-chip--3p-local', tint: '#fff5fc' },
  THREE_P_GLOBAL: { label: '3P Global', className: 'ec-chip--3p-global', tint: '#faf5ff' },
  COMPETITOR: { label: 'Competitor', className: 'ec-chip--competitor', tint: '#fff5f5' },
};

/**
 * An event's category, or undefined when it has none.
 *
 * `eventType` is optional on the wire — an event created from a name alone has no type until
 * it is edited. Such an event is NOT given a category of its own: the design and the service
 * know five types and no "untyped" one, so the legend and the Event Type filter list exactly
 * those five. Where an untyped event is still drawn (a calendar chip), it gets the neutral
 * {@link NO_TYPE_CLASS} and no icon; where a type would be named (the table, a card badge),
 * nothing is shown.
 */
export function typeMetaOf(event: Event): TypeMeta | undefined {
  return event.eventType ? TYPE_META[event.eventType] : undefined;
}

export function typeMeta(t: EventType): TypeMeta {
  return TYPE_META[t];
}

/** Chip class for an event with no type: neutral grey, defined in EventCalendar.css. */
export const NO_TYPE_CLASS = 'ec-chip--no-type';

/** The legend's and the Event Type filter's order: the service's five types. */
export const TYPE_ORDER: readonly EventType[] = EVENT_TYPE_ORDER;

export const SUB_TYPE_LABELS: Record<EventSubType, string> = {
  PARTNER: 'Partner',
  PRECISION: 'Precision',
  INDUSTRY_COHORT: 'Industry Cohort',
  DEVELOPER_COHORT: 'Developer Cohort',
  EXECUTIVE: 'Executive',
  SUP_COHORT: 'SUP Cohort',
};

export function subTypeLabel(t: EventSubType | undefined): string {
  return t ? SUB_TYPE_LABELS[t] : '—';
}

/**
 * The draft / published axis, which is what a marketer acts on. Note DRAFT is
 * marketing-visible only, so this label is not a neutral status — it says whether BD
 * can see the event at all.
 */
export const STATUS_LABELS = { DRAFT: 'Draft', PUBLISHED: 'Published' } as const;

/**
 * The execution stages in the order an event moves through them, as the Figma lays them out — the
 * detail page's progress rail and the edit page's stage picker both read this list.
 */
export const LIFECYCLE_STAGES: readonly { stage: EventLifecycleStage; label: string }[] = [
  { stage: 'PLANNING', label: 'Planning' },
  { stage: 'PRE_EVENT_EXECUTION', label: 'Pre-Event Execution' },
  { stage: 'ON_SITE_EXECUTION', label: 'On-Site Execution' },
  { stage: 'POST_EVENT_EXECUTION', label: 'Post-Event Execution' },
];

/**
 * The `lifecycleStage` to put on an UpdateEvent patch: the chosen stage when it differs from the
 * stored one, otherwise nothing.
 *
 * Never on a DRAFT event — the service rejects any stage change there with a ConflictException (a
 * draft is always PLANNING; AWSECEventHubModel event-operations.smithy), and failing the whole
 * save over a field the marketer could not have meant to set would be worse than ignoring it.
 */
export function lifecycleStageToSend(
  published: boolean,
  chosen: EventLifecycleStage | undefined,
  stored: EventLifecycleStage | undefined,
): EventLifecycleStage | undefined {
  return published && chosen && chosen !== stored ? chosen : undefined;
}
